/**
 * The state behind a list screen: the view, the rows, the field catalogue and
 * the saved views.
 *
 * Plan §3.5 asks for one list engine reused everywhere, which means the state
 * has to be area-agnostic too — a screen names its area and gets filtering,
 * sorting, grouping, field selection, Σ, density, saved views, export and
 * import without writing any of them again.
 *
 * The view lives in the URL. That is not decoration: a filtered list is the
 * thing people send each other ("the ones missing a phone number"), and a view
 * that only exists in React state cannot be sent, bookmarked or reloaded.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  deselectMany, isSelected, noSelection, pageState, selectMany, selectedCount, toggleOne,
  type Selection,
} from './selection.js'
import { useSearchParams } from 'react-router-dom'
import {
  decodeView, emptyView, encodeView, flattenConditions, isFilterGroup, isRelatedCondition,
  type AggregateFunction, type BulkResult, type BulkSelector, type Density,
  type FieldIcon, type FilterCondition, type FilterGroup, type FilterNode,
  type FilterOperator, type FilterToken, type ListView, type SortSpec,
} from '@rowted/shared'
import { request as tenantRequest, type RequestOptions } from './api.js'
import { usePhone } from './use-narrow.js'

/** The viewer's today, which is what a date filter's presets count back from. */
const today = (): string => new Date().toISOString().slice(0, 10)

/**
 * Put `name` in the slot the insertion line was drawn in.
 *
 * `before` is an index *in the list as it looks now* — the column the green
 * line is to the left of, or the length to drop past the end. Taking it that
 * way is the whole fix: pulling the column out first shifts every index after
 * it down by one, so a drag to the right used to land one column short of the
 * line that promised it.
 *
 * Pure, and exported, so the arithmetic is testable without a table.
 */
export function reorderNames(names: string[], name: string, before: number): string[] {
  const from = names.indexOf(name)
  if (from < 0) return names

  const target = Math.max(0, Math.min(names.length, before))
  // Already there: the slot before itself, and the slot after itself.
  if (target === from || target === from + 1) return names

  const next = [...names]
  next.splice(from, 1)
  next.splice(target > from ? target - 1 : target, 0, name)
  return next
}

/**
 * The one condition a field has at the top level, or null.
 *
 * Top level only, and deliberately. The quick filter writes at most one
 * condition per field, so reaching into a group to find "the" condition for a
 * column would find one the popover cannot represent and could not put back.
 * A filter with a group in it is the advanced builder's, and the popover
 * leaves it alone.
 */
export function conditionFor(
  group: FilterGroup | undefined, field: string,
): FilterCondition | null {
  const found = (group?.conditions ?? []).find((node) =>
    !isFilterGroup(node) && !isRelatedCondition(node) && node.field === field)
  return (found as FilterCondition | undefined) ?? null
}

/**
 * That condition replaced, added or removed, leaving everything else alone.
 *
 * Order is kept: a field that already had a condition keeps its place in the
 * list rather than jumping to the end when its value changes, which is what
 * makes the toolbar chips stay still while somebody ticks chips.
 */
export function withFieldCondition(
  group: FilterGroup | undefined, field: string, condition: FilterCondition | null,
): FilterGroup {
  const base = group ?? { match: 'and' as const, conditions: [] }
  const mine = (node: FilterNode) =>
    !isFilterGroup(node) && !isRelatedCondition(node) && node.field === field

  if (!condition) {
    return { ...base, conditions: base.conditions.filter((node) => !mine(node)) }
  }
  if (base.conditions.some(mine)) {
    return {
      ...base,
      conditions: base.conditions.map((node) => (mine(node) ? condition : node)),
    }
  }
  return { ...base, conditions: [...base.conditions, condition] }
}

/**
 * Pin a column left, or unpin it — and keep the pinned ones leftmost.
 *
 * A pinned column is pinned *to the left edge*, so one sitting fifth in the
 * order would have to jump over four unpinned columns to get there and the
 * table would draw it on top of them. Pinning therefore moves it to sit after
 * the other pinned ones.
 */
export function pinColumn(
  pinned: string[], fields: string[], name: string,
): { pinned: string[]; fields: string[] } {
  if (pinned.includes(name)) {
    return { pinned: pinned.filter((entry) => entry !== name), fields }
  }

  const next = [...fields]
  const from = next.indexOf(name)
  const to = next.filter((field) => pinned.includes(field)).length

  if (from >= 0 && from !== to) {
    next.splice(from, 1)
    next.splice(to, 0, name)
  }

  return { pinned: [...pinned, name], fields: next }
}

/**
 * The areas the list engine serves. A phase adds its area here and to AREAS.
 *
 * The catalogue kinds are here for the same reason Contacts is: §3.5 asks for
 * one list engine reused everywhere, and a screen that is nearly like the
 * others is worse than one that is exactly like them.
 */
export type ListArea =
  | 'contacts' | 'properties' | 'jobs' | 'visits'
  | 'items' | 'catalog-admin/services' | 'catalog-admin/packages'
  | 'catalog-admin/divisions' | 'catalog-admin/labor-rates'
  | 'catalog-admin/deployment-costs' | 'catalog-admin/rentals'
  | 'catalog-admin/measurement-types' | 'catalog-admin/units'
  | 'catalog-admin/equipment-types' | 'catalog-admin/equipment-catalog'
  | 'catalog-admin/item-categories' | 'catalog-admin/terms'
  | 'catalog-admin/lead-sources'
  | 'equipment' | 'work-orders'
  | 'invoices'
  /*
   * Rowted Admin's own, which reach `/api/v1/platform/…` by being handed
   * `adminRequest` as `options.request` — the area name is the same shape,
   * only the client underneath it differs.
   *
   * `admin-activity` is mounted twice: once with a base filter of one admin,
   * as their Activity tab, and once bare as the Audit log page. `audit` is
   * kept as its own name for one release so a saved view filed under it is
   * not orphaned.
   */
  | 'clients' | 'audit' | 'email-log'
  | 'admins' | 'admin-activity' | 'admin-sessions'
  | 'client-people' | 'client-activity' | 'client-sessions'
  /* Activity's two (`docs/activity.md` §8.0) — the one touch the engine takes for them. */
  | 'tasks' | 'activity-feed'

export interface ListFieldDef {
  name: string
  label: string
  kind: 'text' | 'number' | 'money' | 'date' | 'datetime' | 'boolean' | 'enum' | 'uuid' | 'relation'
  /**
   * Whether it can be a *column*, as against merely filtered on.
   *
   * A relation field is an id: worth filtering by, never worth printing. The
   * Fields panel is the only place that reads this; every filter menu offers
   * whatever is `filterable`.
   */
  selectable?: boolean
  filterable: boolean
  groupable: boolean
  sortable: boolean
  aggregatable: boolean
  numeric: boolean
  /** Which glyph its header wears, and what a screen reader calls it. */
  icon: FieldIcon
  /*
   * Everything below comes from the server's own catalogue, so which control
   * a field gets is decided once, where the field is declared. A screen that
   * chose for itself would be a screen that disagreed with the next one.
   */
  /** What it points at, when the kind is `relation`. */
  relation?: { area: string; label: string }
  /**
   * What an enum's stored values are called — "PA" is read "PA ·
   * Pennsylvania". The chips still come from `distinct`; this only names
   * them, and a value with no entry prints as itself.
   */
  options?: { value: string; label: string; sub?: string }[]
  /** Opens as a section in the quick filter without being asked for. */
  quick?: boolean
  /** One-tap ranges — "Over $10k". */
  presets?: { label: string; op: FilterOperator; value: unknown }[]
  /** `me` / `my_branch`, for a people or branch relation. */
  tokens?: FilterToken[]
  /** Its heading in More fields and in the field picker. */
  group?: string
}

/** A list this row owns, which a "has a visit where…" condition asks about. */
export interface ListRelationDef {
  key: string
  /** Singular: "visit", "invoice". */
  label: string
}

export interface SavedView {
  id: string
  name: string
  shared: boolean
  isDefault: boolean
  sortOrder: number
  definition: ListView
  ownerId: string | null
  isMine: boolean
}

export interface ListRow extends Record<string, unknown> { id: string }

export interface ListGroup {
  key: string
  label: string
  count: number
  aggregates: Record<string, unknown>
}

interface ListPayload {
  items: ListRow[]
  groups: ListGroup[]
  totals: Record<string, unknown>
  visibleCount: number
  totalCount: number
  nextCursor: string | null
  fields: string[]
}

export interface UseListResult {
  view: ListView
  setView: (next: ListView) => void
  /** The columns actually shown, after the area's defaults and permissions. */
  /** Which list this is — what `/distinct` and `/lookup` are asked of. */
  area: ListArea
  fields: ListFieldDef[]
  visibleFields: ListFieldDef[]
  /** What a related-record condition can be asked about from this list. */
  relations: ListRelationDef[]
  rows: ListRow[]
  groups: ListGroup[]
  totals: Record<string, unknown>
  visibleCount: number
  totalCount: number
  loading: boolean
  error: string | null
  search: string
  setSearch: (value: string) => void
  savedViews: SavedView[]
  activeViewId: string | null
  openSavedView: (id: string | null) => void
  saveView: (name: string, shared: boolean) => Promise<void>
  /** Write the view as it stands now over a saved one, keeping its id. */
  updateView: (id: string) => Promise<void>
  /** Change a saved view's name, and nothing else about it. */
  renameView: (id: string, name: string) => Promise<void>
  deleteView: (id: string) => Promise<void>
  /**
   * Make a view the one this person lands on for this list, or clear it.
   *
   * Per person, not per view: a shared view flagged on its own row would be
   * everybody's default, and what somebody wants is "start me on the one I
   * always use" — often a view a colleague built.
   */
  setDefaultView: (id: string | null) => Promise<void>
  /** True when the view differs from the saved view it was opened from. */
  dirty: boolean
  /**
   * Keep the settings this list is wearing — for me, or for everyone.
   *
   * Only does anything on a list given a `listKey`; a list whose views are
   * tabs keeps its settings by saving one.
   */
  rememberView: (audience: 'me' | 'everyone') => Promise<void>
  /** Drop my own remembered settings and fall back to the company's. */
  forgetView: () => Promise<void>
  rememberedMine: boolean
  reload: () => void
  /*
   * Selection. Deliberately not in the URL: a filter is a thing people send
   * each other, and a tick is a thing one person is doing right now.
   *
   * It survives a page, a sort, a row height and a column change, because
   * none of those alter *which rows match* — v3 §2.2, and the reason the
   * effect that used to drop every tick not on the current page is gone. It
   * is cleared by a change to the filter, the search or the saved view, which
   * do: a tick on a row the question no longer describes is a tick nobody can
   * find to untick.
   */
  selection: Selection
  /** How many are ticked — `visibleCount` minus the exceptions in `matching`. */
  selectedCount: number
  isSelected: (id: string) => boolean
  toggleSelected: (id: string) => void
  /** Ticks this page *on top of* what is already ticked; unticks it if it is all in. */
  togglePage: () => void
  selectPage: () => void
  /** Everything the filter and search match — a mode, not a list of ids. */
  selectAllMatching: () => void
  clearSelection: () => void
  /**
   * Replace the selection with exactly these ids.
   *
   * One caller: "Keep these 2 selected" after a bulk action skipped two
   * records (§3.4). It is a *replacement* rather than an addition, because
   * the point is to be left holding only the ones that still need something.
   */
  keepOnly: (ids: string[]) => void
  /**
   * Kept for one release for the pages that still read a plain set.
   *
   * It is `selection.ids` in `ids` mode and **empty** in `matching` mode,
   * which is exactly why it is going: a page reading this cannot tell "none
   * ticked" from "all 422 ticked", and acting on an empty set is the safe
   * half of that confusion rather than the right answer.
   */
  selected: ReadonlySet<string>
  bulk: (action: 'deactivate' | 'reactivate', ids: string[]) => Promise<number>
  /** v3 §3.4: one verb, one selector, one `BulkResult`. */
  runBulk: (key: string, input?: unknown) => Promise<BulkResult>
  /** Whether this list has an archive flag to act on at all. */
  archivable: boolean
  // Narrow helpers, so screens never hand-assemble a ListView.
  toggleField: (name: string) => void
  setGroup: (name: string | undefined) => void
  toggleSort: (name: string, additive?: boolean) => void
  setSort: (sort: SortSpec[]) => void
  setFilter: (conditions: FilterNode[]) => void
  setAggregate: (field: string, fn: AggregateFunction | null) => void
  setDensity: (density: Density) => void
  /** Drops a column at a position — what dragging a header comes down to. */
  /** Drops a column into the slot the insertion line is drawn in. */
  moveField: (name: string, before: number) => void
  /** A width somebody dragged; zero clears it back to the default. */
  setColumnWidth: (name: string, width: number) => void
  /** Pin a column left, beyond the mark column that is always pinned. */
  togglePin: (name: string) => void
  /** ⇧-click over a span of rows. */
  toggleRange: (fromKey: string, toKey: string) => void

  /** The values a column actually holds, for a filter's chip picker. */
  distinct: (field: string) => Promise<string[]>
  /** The same values with their counts, narrowed by the rest of the filter. */
  countedValues: (field: string, narrowedBy?: FilterGroup) =>
    Promise<{ value: string; count: number }[]>
  /**
   * The names of the records a filter mentions, keyed `field\u0000id`.
   *
   * Filled by `useRelationLabels` for the ids a condition carries without a
   * label of its own — an older saved view, a pasted link, an id typed by
   * hand. `null` for an id this person cannot see, which is a different
   * answer from "not yet known" and is why the map is nullable (v3 §4.4).
   */
  labelCache: ReadonlyMap<string, string | null>
  /** A relation field's mini search, answered from this list. */
  lookup: (field: string, q: string, narrowedBy?: FilterGroup) =>
    Promise<{ id: string; label: string; sub: string | null; count: number }[]>
  /**
   * How many rows a filter would match, without applying it.
   *
   * What the advanced builder shows while somebody is still building. It runs
   * the real query with a page size of one and reads the count off it, so the
   * number cannot disagree with what applying the filter would produce.
   */
  countMatching: (filter: FilterGroup) => Promise<number>

  /* Paging. Deliberately not in the view — see where it is declared. */
  page: number
  pageSize: number
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  pageCount: number

  /*
   * Appending, which is what a phone does instead of paging
   * (`docs/phone-list-loading.md`). Pagination is a desktop idea: nobody
   * thinks in pages of fifty on a 390px screen, and the one anchored thing
   * the bottom edge can hold is already the tab bar.
   */
  /** Whether there are rows past the ones in `rows`. */
  hasMore: boolean
  /** A page after the first is on its way; `loading` stays false. */
  loadingMore: boolean
  /** The last append failed, so the card offers to try again. */
  moreFailed: boolean
  loadMore: () => void
}

/**
 * Two views are the same view if they say the same thing.
 *
 * Key order is not part of what a view says, and a saved one comes back from
 * the server through a schema that reorders it — so a plain `JSON.stringify`
 * compare reported "Edited · not saved" the instant a view was saved.
 */
const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, entry]) => [key, canonical(entry)]))
  }
  return value
}

const sameView = (a: ListView, b: ListView): boolean =>
  JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))

interface FieldCatalogue {
  fields: ListFieldDef[]
  defaultFields: string[]
  relations?: ListRelationDef[]
}

/**
 * The field catalogue, asked for once per area per page load.
 *
 * It is a pure function of the area definition and what this person may see:
 * neither changes while the tab is open, so clicking Jobs → Invoices → Jobs
 * asked the same question three times and waited for the answer each time.
 * The promise is cached rather than the value, so two lists mounting at once
 * (a screen and a related list on the same area) share the one request.
 *
 * Saved views are deliberately *not* cached beside it — somebody saves,
 * renames and deletes those, and a stale tab row is a tab row that argues
 * with the view it is showing.
 */
const catalogues = new Map<string, Promise<FieldCatalogue>>()

function fieldCatalogue(
  area: ListArea,
  send: <T>(path: string, options?: RequestOptions) => Promise<T>,
): Promise<FieldCatalogue> {
  const held = catalogues.get(area)
  if (held) return held

  const asking = send<FieldCatalogue>(`/${area}/fields`)
    // A failure is not cached: the next mount asks again rather than
    // remembering for the rest of the session that this list is broken.
    .catch((cause: unknown) => { catalogues.delete(area); throw cause })
  catalogues.set(area, asking)
  return asking
}

/** One frozen empty set, so the shim does not make a new one per render. */
const EMPTY_IDS: ReadonlySet<string> = new Set()

export function useList(
  area: ListArea,
  options: {
    /**
     * Columns to fetch whether or not they are shown.
     *
     * A screen's metric cards count things like "how many are bill-to", and
     * reading that off a row only works if the column came back. Without this
     * the card silently showed zero — a wrong number, which is worse than no
     * number.
     */
    alwaysFetch?: string[]
    /**
     * The view to open on when the URL carries none — the Jobs board opens
     * grouped by status, as §6.3 draws it.
     *
     * Applied without writing to the URL, so the address bar stays clean until
     * the person changes something and an unmodified board is still shareable.
     */
    defaultView?: Partial<ListView>
    /**
     * Where the view lives.
     *
     * `url` is the list that *is* the page: its view is in the address bar, so
     * a filtered list is a link somebody can send. `local` is a list inside a
     * record page — a property's jobs, a contact's invoices. There are three of
     * those on a page and one `?view=`, so putting them in the URL would have
     * them overwrite each other; and nobody sends a link to the second tab of
     * somebody else's contact.
     */
    storage?: 'url' | 'local'
    /**
     * Append each page to the last rather than replacing it.
     *
     * Defaults to whether this is a phone.
     *
     * The fetch is the same one the pager makes; what changes is what happens
     * to the result. Page 0 always replaces, so any change to the question
     * starts the list again rather than growing a second answer onto the
     * first.
     */
    accumulate?: boolean
    /**
     * What this list is, for the settings it remembers.
     *
     * A related list has no tab row, so "keep these settings" is one
     * definition rather than a view with a name — filed under this key, per
     * person and per tenant. See `/views/remembered` on the server.
     */
    listKey?: string
    /**
     * The condition that makes it *this* record's list, always applied and
     * never shown.
     *
     * It is not part of the view: it is not a filter somebody chose, it is
     * what the list is about, and a chip offering to remove it would offer to
     * turn a property's jobs into every job in the company.
     */
    baseFilter?: FilterCondition[]
    /**
     * The fetch this list uses.
     *
     * Defaults to the office app's, which is every list but one. Rowted
     * Admin's Clients screen passes `adminRequest`, and every URL these hooks
     * build then lands under `/api/v1/platform` with the platform CSRF header
     * — `docs/rowted-admin-v2.md` §2.8. Injected rather than switched on a
     * flag for the same reason the server has a runner: a tenant's scope must
     * never end up on a request that reads every tenant.
     */
    request?: <T>(path: string, options?: RequestOptions) => Promise<T>
  } = {},
): UseListResult {
  const send = options.request ?? tenantRequest
  const [params, setParams] = useSearchParams()
  const local = options.storage === 'local'
  /*
   * Appending is the phone's default, decided here rather than at each of the
   * ten `useList` call sites — one of them would have been forgotten, and a
   * list that pages on a phone is the bug this is fixing. A screen that wants
   * the other behaviour still says so.
   */
  const phone = usePhone()
  const accumulate = options.accumulate ?? phone

  /*
   * One of these is live and the other is inert, decided by `storage`. Hooks
   * cannot be called conditionally, so both exist and `setView` below picks.
   */
  const [localView, setLocalView] = useState<string | null>(null)
  const [localSearch, setLocalSearch] = useState('')

  const encoded = local ? localView : params.get('view')
  const activeViewId = local ? null : params.get('saved')
  const search = local ? localSearch : (params.get('q') ?? '')

  const defaultViewKey = JSON.stringify(options.defaultView ?? null)
  const view = useMemo(
    () => (encoded
      ? decodeView(encoded)
      : { ...emptyView(), ...(JSON.parse(defaultViewKey) as Partial<ListView> | null) }),
    [encoded, defaultViewKey])

  const [fields, setFields] = useState<ListFieldDef[]>([])
  const [relations, setRelations] = useState<ListRelationDef[]>([])
  const [defaultFields, setDefaultFields] = useState<string[]>([])
  const [savedViews, setSavedViews] = useState<SavedView[]>([])
  /** Whether the default view has already had its one chance to open. */
  const landed = useRef(false)
  const [payload, setPayload] = useState<ListPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  const [selection, setSelection] = useState<Selection>(noSelection)

  /*
   * The page, and how big a page is.
   *
   * Not in the view and not in the URL. A view is a saved question — "the ones
   * missing a phone number" — and which page of the answer somebody happened
   * to be on is not part of it. Sending a link to page 4 of a list whose rows
   * have since changed is sending a link to the wrong rows.
   */
  const [page, setPage] = useState(0)
  /*
   * Appending state. `loading` stays the skeleton's — it is true for page 0
   * only — so the cards already on screen do not vanish behind one while the
   * next fifty arrive.
   */
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreFailed, setMoreFailed] = useState(false)
  /* Retrying a failed append re-fetches the *same* page, so it cannot be the
     page number that changes. */
  const [moreToken, setMoreToken] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  /** Writes the view into the URL, replacing rather than stacking history. */
  const setView = useCallback((next: ListView) => {
    if (local) {
      setLocalView(sameView(next, emptyView()) ? null : encodeView(next))
      return
    }
    setParams((current) => {
      const updated = new URLSearchParams(current)
      if (sameView(next, emptyView())) updated.delete('view')
      else updated.set('view', encodeView(next))
      return updated
    }, { replace: true })
  }, [local, setParams])

  const setSearch = useCallback((value: string) => {
    if (local) { setLocalSearch(value); return }
    setParams((current) => {
      const updated = new URLSearchParams(current)
      if (value) updated.set('q', value)
      else updated.delete('q')
      return updated
    }, { replace: true })
  }, [local, setParams])

  const openSavedView = useCallback((id: string | null) => {
    const saved = id ? savedViews.find((candidate) => candidate.id === id) : null
    setParams((current) => {
      const updated = new URLSearchParams(current)
      if (saved) {
        updated.set('saved', saved.id)
        updated.set('view', encodeView(saved.definition))
      } else {
        updated.delete('saved')
        updated.delete('view')
      }
      return updated
    }, { replace: true })
  }, [savedViews, setParams])

  // --- the field catalogue and saved views, once per area ------------------

  useEffect(() => {
    let cancelled = false

    void Promise.all([
      fieldCatalogue(area, send),
      send<{ items: SavedView[] }>(`/${area}/views`),
    ]).then(([catalogue, views]) => {
      if (cancelled) return
      /*
       * `?? []` on all three: a response that is missing one of them puts
       * `undefined` into state, and the very next render does `.map` on it —
       * so a field catalogue that came back the wrong shape takes the whole
       * screen down white rather than drawing an empty list. The server
       * always sends them; the cost of being sure is two characters.
       */
      setFields(catalogue.fields ?? [])
      setDefaultFields(catalogue.defaultFields ?? [])
      setRelations(catalogue.relations ?? [])
      setSavedViews(views.items ?? [])

      /*
       * Land on this person's default, if they set one and the URL is not
       * already asking for something else.
       *
       * Only when the address bar is bare. A link somebody sent, a back
       * button, a card click — all of those put a view in the URL, and
       * overriding them with a default would make a shared link open the wrong
       * list for whoever received it.
       */
      if (landed.current) return
      landed.current = true
      const fallback = views.items.find((entry) => entry.isDefault)
      if (fallback && !encoded && !activeViewId) {
        setParams((current) => {
          const updated = new URLSearchParams(current)
          updated.set('saved', fallback.id)
          updated.set('view', encodeView(fallback.definition))
          return updated
        }, { replace: true })
      }
    }).catch((cause: unknown) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load this list.')
    })

    return () => { cancelled = true }
  }, [area])

  // --- the rows ------------------------------------------------------------

  // Typing in the search box should not fire a request per keystroke, and an
  // earlier response must never overwrite a later one.
  const requestId = useRef(0)

  const alwaysFetch = options.alwaysFetch
  const alwaysKey = alwaysFetch?.join(',') ?? ''

  /*
   * What the list is about, added on the way out and never in the view.
   *
   * Keeping it out of `view` is the point: the filter chips, the step filter
   * and "keep these settings" all read the view, and a base condition in
   * there would show up as a removable chip saying "Property is <uuid>".
   */
  const baseKey = JSON.stringify(options.baseFilter ?? null)
  const withBase = useCallback((current: ListView): ListView => {
    const base = JSON.parse(baseKey) as FilterCondition[] | null
    if (!base || base.length === 0) return current
    const own = current.filter?.conditions ?? []
    return {
      ...current,
      filter: { match: 'and', conditions: [...base, ...own] },
    }
  }, [baseKey])

  /*
   * The settings a related list opens with — mine if I kept any, otherwise the
   * company's. Fetched once, and only for a list that has a key to file them
   * under.
   */
  const listKey = options.listKey
  const [remembered, setRemembered] = useState<{ loaded: boolean; mine: boolean }>(
    { loaded: !listKey, mine: false })

  useEffect(() => {
    if (!listKey) return undefined
    let cancelled = false
    send<{ definition: ListView | null; mine: boolean }>(`/${area}/views/remembered`)
      .then((result) => {
        if (cancelled) return
        if (result.definition) setLocalView(encodeView(result.definition))
        setRemembered({ loaded: true, mine: result.mine })
      })
      .catch(() => { if (!cancelled) setRemembered({ loaded: true, mine: false }) })
    return () => { cancelled = true }
  }, [area, listKey])

  const rememberView = useCallback(async (audience: 'me' | 'everyone') => {
    if (!listKey) return
    await send(`/${area}/views/remembered`, {
      method: 'PUT',
      body: { definition: view, audience },
    })
    if (audience === 'me') setRemembered({ loaded: true, mine: true })
  }, [area, listKey, view])

  const forgetView = useCallback(async () => {
    if (!listKey) return
    await send(`/${area}/views/remembered`, { method: 'DELETE' })
    const result = await send<{ definition: ListView | null; mine: boolean }>(
      `/${area}/views/remembered`)
    setLocalView(result.definition ? encodeView(result.definition) : null)
    setRemembered({ loaded: true, mine: result.mine })
  }, [area, listKey])

  /*
   * Rows belong to the question that fetched them.
   *
   * A record page draws every related tab with the same `<RelatedList>` at the
   * same place in the tree, so React reconciles rather than remounts when the
   * tab changes: the component keeps its state and only `area` changes. The
   * rows in `payload` are then the *previous* tab's, and they are rendered
   * under the new tab's columns, marks and formatters for the one frame before
   * the effect below runs — which is how a property's Invoices tab briefly
   * drew seven jobs: `J-12661 · Written off` under an INVOICE heading with an
   * invoice ring beside it and every money column an em dash, because a job
   * row has no amount or balance.
   *
   * Clearing it in an effect would not help: an effect runs *after* that
   * paint. So it is cleared during the render that first sees a new question,
   * which is React's own answer to deriving state from props and is why
   * `loading` goes back up here too — otherwise the table takes the empty rows
   * for an answer and draws "Nothing here yet" over a list that is loading.
   */
  const question = `${area}\u0000${listKey ?? ''}\u0000${baseKey}`
  const asked = useRef(question)
  if (asked.current !== question) {
    asked.current = question
    setPayload(null)
    setLoading(true)
    setError(null)
    setSelection(noSelection)

    /*
     * And everything that decides *what to ask*, which is the half of this
     * that clearing the rows alone did not fix.
     *
     * The guards on the fetch below — "wait for the catalogue", "wait for the
     * kept settings" — are the right guards and they could never fire on a tab
     * change, because the state they test still held the **previous** tab's
     * answers: `defaultFields` was non-empty, `remembered.loaded` was true, and
     * `localView` still carried the view the last tab had kept. So the first
     * request for the new tab went out immediately, asking for the old tab's
     * columns; the engine dropped the ones the new area does not have and
     * filled the gap from the area's own defaults, which is why a property's
     * Jobs tab drew Status · Service line · Customer · Property · Zone with no
     * **Job** column at all, and its Invoices tab drew everything except the
     * invoice. The right request followed a moment later and replaced it,
     * which is why it looked like a flicker and why clicking through the tabs
     * faster than that made it stick.
     *
     * Cleared here, the guards hold, and the first request a tab makes is the
     * only one it needs.
     */
    setLocalView(null)
    setFields([])
    setRelations([])
    setDefaultFields([])
    setSavedViews([])
    setRemembered({ loaded: !listKey, mine: false })
    landed.current = false
  }

  useEffect(() => {
    // Wait for the catalogue: until the area's default fields are known there
    // is no way to add a column to them without dropping the rest.
    if (defaultFields.length === 0) return undefined
    // And for the remembered settings, or the first request is for the view
    // this list is about to stop showing.
    if (!remembered.loaded) return undefined

    const id = requestId.current + 1
    requestId.current = id
    const appending = accumulate && page > 0
    if (appending) setLoadingMore(true)
    else setLoading(true)

    const shown = view.fields ?? defaultFields
    const fetched = alwaysKey
      ? [...new Set([...shown, ...alwaysKey.split(',')])]
      : view.fields

    const timer = setTimeout(() => {
      send<ListPayload>(`/${area}/list`, {
        method: 'POST',
        body: {
          view: withBase({ ...view, fields: fetched }),
          /*
           * The same conditions again, named for what they are. The view
           * carries them so the rows are right; this is so the *count* is —
           * without it a property with two visits read "2 of 4", the 4 being
           * every visit in the tenant (`list-engine.ts`, `ListRequest.baseFilter`).
           */
          ...(options.baseFilter?.length ? { baseFilter: options.baseFilter } : {}),
          search: search || undefined,
          limit: pageSize,
          offset: page * pageSize,
          // Our today, so "this week" is the week we are in rather than the
          // week the server happens to be in.
          asOf: today(),
        },
      }).then((result) => {
        if (requestId.current !== id) return

        if (appending) {
          setPayload((current) => (current
            ? { ...result, items: [...current.items, ...result.items] }
            : result))
        } else {
          setPayload(result)
          /*
           * And the ticks stay.
           *
           * This used to drop every id the new page did not return, on the
           * reasoning that a tick you cannot see is a tick you cannot untick.
           * That reasoning is right about a *filter* and wrong about a
           * *page*: turning to page 2 and back used to empty a selection
           * somebody had spent a minute building, which is why nobody could
           * act on more than fifty rows. Crossing pages is the feature now
           * (§2.2), and what clears a selection is a change to the question
           * — the filter, the search, the saved view — handled below.
           */
        }
        setError(null)
        setMoreFailed(false)
      }).catch((cause: unknown) => {
        if (requestId.current !== id) return
        /*
         * A page that failed on its way in is not a list that failed: the
         * fifty rows already on screen are fine, and blanking them for an
         * error banner would be the worst of both. The card says "Try again"
         * instead.
         */
        if (appending) setMoreFailed(true)
        else setError(cause instanceof Error ? cause.message : 'Could not load this list.')
      }).finally(() => {
        if (requestId.current !== id) return
        setLoading(false)
        setLoadingMore(false)
      })
    }, search ? 200 : 0)

    return () => clearTimeout(timer)
  }, [area, view, search, reloadToken, moreToken, defaultFields, alwaysKey, page, pageSize,
    withBase, remembered.loaded, accumulate])

  // --- filter labels -------------------------------------------------------

  /*
   * Names for the ids a filter mentions and does not already name.
   *
   * It runs after the filter changes, one request per field, and it
   * deliberately **does not touch the view** — filling in a label is not an
   * edit, and marking the view dirty would offer to save a change nobody
   * made. `saveView` folds the cache back in when somebody really does save,
   * so a view heals itself the next time it is written rather than on every
   * page load.
   */
  const [labelCache, setLabelCache] = useState<ReadonlyMap<string, string | null>>(new Map())

  useEffect(() => {
    const wanted = new Map<string, Set<string>>()
    for (const condition of flattenConditions(view.filter)) {
      const field = fields.find((one) => one.name === condition.field)
      if (field?.kind !== 'relation' && field?.kind !== 'uuid') continue
      const values = Array.isArray(condition.value) ? condition.value : [condition.value]
      for (const value of values) {
        if (typeof value !== 'string') continue
        if (condition.labels?.[value] !== undefined) continue
        if (labelCache.has(`${condition.field}\u0000${value}`)) continue
        const set = wanted.get(condition.field) ?? new Set()
        set.add(value)
        wanted.set(condition.field, set)
      }
    }
    if (wanted.size === 0) return

    let cancelled = false
    void Promise.all([...wanted].map(async ([field, idSet]) => {
      const ids = [...idSet].slice(0, 50)
      try {
        const result = await send<{ rows: { id: string; label: string }[] }>(
          `/${area}/lookup?field=${encodeURIComponent(field)}&ids=${ids.join(',')}`)
        const found = new Map(result.rows.map((row) => [row.id, row.label]))
        // An id the answer did not carry is one this person cannot see:
        // `null`, so the chip says so rather than asking again on every render.
        return ids.map((id) => [`${field}\u0000${id}`, found.get(id) ?? null] as const)
      } catch {
        return [] as (readonly [string, string | null])[]
      }
    })).then((pairs) => {
      if (cancelled) return
      setLabelCache((current) => {
        const next = new Map(current)
        for (const entry of pairs.flat()) next.set(entry[0], entry[1])
        return next
      })
    })
    return () => { cancelled = true }
  }, [area, fields, view.filter, labelCache])

  // --- saved views ---------------------------------------------------------

  /*
   * The view as it is, with the names filled in (v3 §4.4).
   *
   * A view **heals itself the next time it is saved** rather than on every
   * page load: filling in a label is not an edit, so the repair effect
   * deliberately leaves `view` alone and the cache is folded back in only at
   * the moment somebody really is writing the thing down.
   */
  const healed = useCallback((): ListView => {
    if (!view.filter || labelCache.size === 0) return view
    const fix = (nodes: FilterNode[]): FilterNode[] => nodes.map((node) => {
      if (isFilterGroup(node)) return { ...node, conditions: fix(node.conditions) }
      if (isRelatedCondition(node)) return node
      const condition = node
      const values = Array.isArray(condition.value) ? condition.value : [condition.value]
      const found: Record<string, string> = {}
      for (const value of values) {
        if (typeof value !== 'string') continue
        if (condition.labels?.[value] !== undefined) continue
        const name = labelCache.get(`${condition.field}\u0000${value}`)
        if (typeof name === 'string') found[value] = name
      }
      return Object.keys(found).length === 0
        ? condition
        : { ...condition, labels: { ...(condition.labels ?? {}), ...found } }
    })
    return { ...view, filter: { ...view.filter, conditions: fix(view.filter.conditions) } }
  }, [view, labelCache])

  const saveView = useCallback(async (name: string, shared: boolean) => {
    const created = await send<{ id: string }>(`/${area}/views`, {
      method: 'POST',
      body: { name, definition: healed(), shared },
    })
    const refreshed = await send<{ items: SavedView[] }>(`/${area}/views`)
    setSavedViews(refreshed.items)
    setParams((current) => {
      const updated = new URLSearchParams(current)
      updated.set('saved', created.id)
      return updated
    }, { replace: true })
  }, [area, healed, setParams])

  /*
   * Overwrite rather than replace. The id is what `user_list_defaults` points
   * at, so a save-and-remove would quietly unset the default of everybody who
   * lands on this view.
   */
  const updateView = useCallback(async (id: string) => {
    const definition = healed()
    await send(`/${area}/views/${id}`, { method: 'PATCH', body: { definition } })
    setSavedViews((current) => current.map((candidate) => (
      candidate.id === id ? { ...candidate, definition } : candidate)))
  }, [area, healed])

  const renameView = useCallback(async (id: string, name: string) => {
    await send(`/${area}/views/${id}`, { method: 'PATCH', body: { name } })
    setSavedViews((current) => current.map((candidate) => (
      candidate.id === id ? { ...candidate, name } : candidate)))
  }, [area])

  const deleteView = useCallback(async (id: string) => {
    await send(`/${area}/views/${id}`, { method: 'DELETE' })
    setSavedViews((current) => current.filter((candidate) => candidate.id !== id))
    if (activeViewId === id) openSavedView(null)
  }, [area, activeViewId, openSavedView])

  const setDefaultView = useCallback(async (id: string | null) => {
    await send(`/${area}/views/default`, { method: 'PUT', body: { viewId: id } })
    setSavedViews((current) => current.map((candidate) => ({
      ...candidate,
      isDefault: candidate.id === id,
    })))
  }, [area])

  // --- view edits ----------------------------------------------------------

  const visibleFieldNames = view.fields ?? defaultFields

  const toggleField = useCallback((name: string) => {
    const current = view.fields ?? defaultFields
    const next = current.includes(name)
      ? current.filter((field) => field !== name)
      // Put a re-added column back where the area puts it, not at the end, so
      // toggling a field off and on again does not reorder the table.
      : fields.filter((field) => current.includes(field.name) || field.name === name)
        .map((field) => field.name)
    // A list with no columns is not a list; the last one stays.
    if (next.length > 0) setView({ ...view, fields: next })
  }, [view, defaultFields, fields, setView])

  const setGroup = useCallback((name: string | undefined) => {
    setView({ ...view, group: name })
  }, [view, setView])

  /** Click a header: ascending, then descending, then unsorted. */
  /**
   * Click a header: ascending, descending, off. ⇧-click adds a second sort
   * rather than replacing the first — which is the only way to say "oldest
   * first, and within a day by customer".
   */
  const toggleSort = useCallback((name: string, additive = false) => {
    const existing = view.sort.find((spec) => spec.field === name)

    if (!additive) {
      if (!existing) setView({ ...view, sort: [{ field: name, direction: 'asc' }] })
      else if (existing.direction === 'asc') {
        setView({ ...view, sort: [{ field: name, direction: 'desc' }] })
      } else setView({ ...view, sort: [] })
      return
    }

    const others = view.sort.filter((spec) => spec.field !== name)
    if (!existing) setView({ ...view, sort: [...view.sort, { field: name, direction: 'asc' }] })
    else if (existing.direction === 'asc') {
      setView({
        ...view,
        sort: view.sort.map((spec) =>
          (spec.field === name ? { ...spec, direction: 'desc' as const } : spec)),
      })
    } else setView({ ...view, sort: others })
  }, [view, setView])

  /**
   * A width somebody dragged. Zero clears it, which is what double-clicking
   * the edge means: back to the column's own idea of how wide it should be.
   */
  const setColumnWidth = useCallback((name: string, width: number) => {
    const widths = { ...(view.columnWidths ?? {}) }
    if (width <= 0) delete widths[name]
    else widths[name] = width
    setView({
      ...view,
      columnWidths: Object.keys(widths).length > 0 ? widths : undefined,
    })
  }, [view, setView])

  /**
   * Pin a column left, or unpin it.
   *
   * One at a time: the mark column is always pinned and the action cell always
   * is, so a second user pin already leaves very little scrolling table. §3.5
   * says "users may pin one more" and this is that, enforced rather than
   * suggested.
   */
  /**
   * Pin a column left, or unpin it. As many as somebody wants.
   *
   * It used to replace the whole list, so pinning Status unpinned Job — and
   * "keep Job, Status and Next action in view while I scroll right" is the
   * entire reason anybody pins anything.
   */
  const togglePin = useCallback((name: string) => {
    const result = pinColumn(view.pinned ?? [], view.fields ?? defaultFields, name)
    setView({
      ...view,
      pinned: result.pinned.length > 0 ? result.pinned : undefined,
      fields: result.fields,
    })
  }, [view, defaultFields, setView])


  const setSort = useCallback((sort: SortSpec[]) => setView({ ...view, sort }), [view, setView])

  /*
   * Any change to the question puts you back on page one. Staying on page 9 of
   * a list that now has two pages shows an empty table, which reads as "no
   * matches" rather than as "you are past the end".
   */
  useEffect(() => { setPage(0) }, [view.filter, view.group, search])

  /*
   * And a change to *which rows match* starts the selection over (§2.2).
   *
   * The filter, the search and the saved view, and nothing else. Grouping is
   * deliberately not in here even though it is in the effect above: it
   * rearranges the same rows rather than choosing different ones, so a tick
   * survives it, while turning to page 9 of a narrower question would leave
   * somebody holding ticks on rows that are no longer in the answer.
   */
  const asking = `${JSON.stringify(view.filter ?? null)}\u0000${search}\u0000${activeViewId ?? ''}`
  const askedRows = useRef(asking)
  useEffect(() => {
    if (askedRows.current === asking) return
    askedRows.current = asking
    setSelection(noSelection)
  }, [asking])

  /*
   * Appending restarts on *any* change to the question, not only the three
   * above. A sort change with a hundred and fifty rows loaded would otherwise
   * fetch row 150 onward of the new order and staple it under the old one.
   * On a desktop the narrower rule stands: re-ordering a column should not
   * throw you back to page one.
   */
  useEffect(() => {
    if (accumulate) setPage(0)
  }, [accumulate, view, search])

  const setFilter = useCallback((conditions: FilterNode[]) => {
    setView({
      ...view,
      filter: conditions.length > 0 ? { match: 'and', conditions } : undefined,
    })
  }, [view, setView])

  const setAggregate = useCallback((field: string, fn: AggregateFunction | null) => {
    const rest = view.aggregates.filter((spec) => spec.field !== field)
    setView({ ...view, aggregates: fn ? [...rest, { field, fn }] : rest })
  }, [view, setView])

  const setDensity = useCallback((density: Density) => {
    setView({ ...view, density })
  }, [view, setView])

  /**
   * Move a column to a position among the shown ones.
   *
   * The shown columns *are* an ordered list — `view.fields` — so reordering is
   * reordering it, and it saves, shares and lands in the URL with everything
   * else about the view for free.
   */
  const moveField = useCallback((name: string, before: number) => {
    const current = view.fields ?? defaultFields
    const next = reorderNames(current, name, before)
    if (next !== current) setView({ ...view, fields: next })
  }, [view, defaultFields, setView])

  const rows = payload?.items ?? []

  /**
   * ⇧-click: everything between the last tick and this one.
   *
   * Reads the range off the rows as they are displayed, not off the ids — a
   * range is a thing you see on the screen, and after a sort the ids are in a
   * different order than the rows.
   */
  const toggleRange = useCallback((fromKey: string, toKey: string) => {
    const order = rows.map((row) => row.id)
    const from = order.indexOf(fromKey)
    const to = order.indexOf(toKey)
    if (from < 0 || to < 0) return

    const span = order.slice(Math.min(from, to), Math.max(from, to) + 1)
    setSelection((current) => selectMany(current, span))
  }, [rows])

  const toggleSelected = useCallback((id: string) => {
    setSelection((current) => toggleOne(current, id))
  }, [])

  const pageIds = useMemo(() => rows.map((row) => row.id), [rows])

  /* What the list says it is showing, which is what `matching` counts. */
  const visibleCount = payload?.visibleCount ?? 0

  /*
   * The page's box **adds** rather than replaces.
   *
   * It used to set the selection to this page's rows, which quietly threw
   * away whatever was ticked on page 1 the moment somebody ticked the box on
   * page 2 — the opposite of the thing §2 is for. Unticking still only
   * unticks this page.
   */
  const togglePage = useCallback(() => {
    setSelection((current) => (pageState(current, pageIds).checked
      ? deselectMany(current, pageIds)
      : selectMany(current, pageIds)))
  }, [pageIds])

  const selectPage = useCallback(() => {
    setSelection((current) => selectMany(current, pageIds))
  }, [pageIds])

  const selectAllMatching = useCallback(() => {
    setSelection({ mode: 'matching', excluded: new Set() })
  }, [])

  const clearSelection = useCallback(() => setSelection(noSelection), [])


  const keepOnly = useCallback((ids: string[]) => {
    setSelection({ mode: 'ids', ids: new Set(ids) })
  }, [])

  const bulk = useCallback(async (
    action: 'deactivate' | 'reactivate', ids: string[],
  ): Promise<number> => {
    const result = await send<{ changed: number }>(`/${area}/archive`, {
      method: 'POST',
      body: { action, ids },
    })
    setSelection(noSelection)
    setReloadToken((token) => token + 1)
    return result.changed
  }, [area])

  /*
   * One verb for every bulk action (§3.4), and it sends a **selector**
   * rather than a list of ids.
   *
   * In `matching` mode the client does not have the ids — it has fifty of
   * four hundred — so it sends the question instead: the view, the search
   * and whatever was unticked, plus `expect`, the number the person was
   * looking at. The server refuses rather than guesses when its own count
   * disagrees, which is the whole reason `expect` is on the wire: between
   * reading "126" and pressing Deactivate, somebody else may have added one.
   *
   * It deliberately does **not** clear the selection or reload: the caller
   * decides, because a result with skipped rows wants "Keep these 2 selected"
   * to still have something to keep.
   */
  const runBulk = useCallback(async (key: string, input?: unknown): Promise<BulkResult> => {
    const selector: BulkSelector = selection.mode === 'ids'
      ? { mode: 'ids', ids: [...selection.ids] }
      : {
        mode: 'matching',
        view,
        search,
        excluded: [...selection.excluded],
        expect: selectedCount(selection, visibleCount),
      }
    return send<BulkResult>(`/${area}/bulk-actions/${key}`, {
      method: 'POST',
      body: { selector, ...(input === undefined ? {} : { input }) },
    })
  }, [area, selection, view, search, visibleCount])

  const activeSaved = savedViews.find((candidate) => candidate.id === activeViewId) ?? null

  return {
    view,
    setView,
    /** Which list this is — what `/distinct` and `/lookup` are asked of. */
    area,
    fields,
    relations,
    visibleFields: visibleFieldNames
      .map((name) => fields.find((field) => field.name === name))
      .filter((field): field is ListFieldDef => Boolean(field)),
    rows,
    groups: payload?.groups ?? [],
    totals: payload?.totals ?? {},
    visibleCount,
    totalCount: payload?.totalCount ?? 0,
    loading,
    error,
    search,
    setSearch,
    savedViews,
    activeViewId,
    openSavedView,
    saveView,
    updateView,
    renameView,
    deleteView,
    setDefaultView,
    dirty: activeSaved ? !sameView(activeSaved.definition, view) : false,
    reload: () => { setPage(0); setReloadToken((token) => token + 1) },
    selection,
    selectedCount: selectedCount(selection, visibleCount),
    isSelected: (id: string) => isSelected(selection, id),
    togglePage,
    selectPage,
    selectAllMatching,
    keepOnly,
    labelCache,
    /* The read-only shim — empty in `matching` mode, on purpose. */
    selected: selection.mode === 'ids' ? selection.ids : EMPTY_IDS,
    runBulk,
    toggleSelected,
    clearSelection,
    bulk,
    archivable: fields.some((field) => field.name === ARCHIVE_FIELD),
    toggleField,
    setGroup,
    toggleSort,
    setSort,
    setFilter,
    setAggregate,
    setDensity,
    setColumnWidth,
    togglePin,
    toggleRange,
    distinct: async (field: string) => {
      const result = await send<{ values: { value: string }[] }>(
        `/${area}/distinct?field=${encodeURIComponent(field)}`)
      return result.values.map((row) => row.value)
    },
    /*
     * The same values with their counts, narrowed by the rest of the filter.
     *
     * `withBase` like the rows themselves: on a property's jobs, "Quoted 14"
     * has to mean 14 of *this property's*, or the number beside the chip is
     * from a list nobody is looking at.
     */
    countedValues: async (field: string, narrowedBy?: FilterGroup) => {
      const result = await send<{ values: { value: string; count: number }[] }>(
        `/${area}/distinct`, {
          method: 'POST',
          body: {
            field,
            narrowedBy: withBase({
              ...view,
              filter: narrowedBy && narrowedBy.conditions.length > 0 ? narrowedBy : undefined,
            }).filter,
          },
        })
      return result.values
    },
    lookup: async (field: string, q: string, narrowedBy?: FilterGroup) => {
      const result = await send<{
        items: { id: string; label: string; sub: string | null; count: number }[]
      }>(`/${area}/lookup`, {
        method: 'POST',
        body: {
          field,
          q: q || undefined,
          narrowedBy: withBase({
            ...view,
            filter: narrowedBy && narrowedBy.conditions.length > 0 ? narrowedBy : undefined,
          }).filter,
        },
      })
      return result.items
    },
    countMatching: async (filter: FilterGroup) => {
      const result = await send<ListPayload>(`/${area}/list`, {
        method: 'POST',
        body: {
          // Through `withBase` like the rows themselves: "42 match" on a
          // property's jobs has to mean 42 of *this property's*.
          view: withBase({ ...view, filter: filter.conditions.length > 0 ? filter : undefined }),
          search: search || undefined,
          limit: 1,
          asOf: today(),
        },
      })
      return result.visibleCount
    },
    rememberView,
    forgetView,
    /** Whether the settings this list opened with are this person's own. */
    rememberedMine: remembered.mine,
    page,
    pageSize,
    setPage,
    setPageSize: (size: number) => { setPageSize(size); setPage(0) },
    pageCount: Math.max(1, Math.ceil((payload?.visibleCount ?? 0) / pageSize)),
    hasMore: accumulate
      ? rows.length < (payload?.visibleCount ?? 0)
      : page < Math.max(1, Math.ceil((payload?.visibleCount ?? 0) / pageSize)) - 1,
    loadingMore,
    moreFailed,
    loadMore: () => {
      if (loading || loadingMore) return
      if (moreFailed) {
        setMoreFailed(false)
        setLoadingMore(true)
        setMoreToken((token) => token + 1)
        return
      }
      if (rows.length >= (payload?.visibleCount ?? 0)) return
      setLoadingMore(true)
      setPage((current) => current + 1)
    },
    moveField,
  }
}

/**
 * The column an archivable area carries. One name across every area, so the
 * bulk bar and the "show deactivated" filter are written once rather than per
 * list — the server decides whether the area has one by whether it is in the
 * catalogue.
 */
export const ARCHIVE_FIELD = 'active'
